/**
 * WebHID transport for the ViMouseJiggler configuration interface.
 *
 * Three rules, all load-bearing (AGENTS.md sections 6.1 and 10.2):
 *
 *  1. NEVER select a device by index. `requestDevice({filters})` filters, but
 *     `getDevices()` does NOT — it returns every granted device, including the
 *     mouse interface, which opens successfully and then fails every I/O call
 *     because its collection is blocklisted and arrives EMPTY. Select by
 *     collection.
 *
 *  2. ONE REQUEST IN FLIGHT. The device answers on a single-buffered input
 *     endpoint and has no request-id, so an overlapping request would be matched
 *     against the wrong response. Every call is queued.
 *
 *  3. THE DEVICE ACKS BEFORE IT WRITES FLASH. SAVE_TO_FLASH returns ACK in tens
 *     of milliseconds and pushes SAVE_RESULT 100-200 ms later; polling GET_CONFIG
 *     immediately after would read `saved: false` and look like a failure.
 */

import {
  DEVICE_FILTER,
  MSG_REPORT_ID_IN,
  MSG_REPORT_ID_OUT,
  MSG_STEPS_PER_FRAME,
  MsgId,
  NACK_REASON_HELP,
  NACK_REASON_NAMES,
  RespId,
  isConfigInterface,
} from '../protocol/constants.ts';
import {
  decodeConfig,
  decodeFrame,
  decodeInfo,
  decodeSaveResult,
  decodeStatus,
  decodeSteps,
  encodeConfig,
  encodeFrame,
  encodeSteps,
} from '../protocol/codec.ts';
import {
  DeviceBusyError,
  DeviceNackError,
  DeviceTimeoutError,
  type DeviceConfig,
  type DeviceInfo,
  type DeviceStatus,
  type Frame,
  type JigStep,
  type SaveResult,
} from '../protocol/types.ts';

/** How long to wait for a response before giving up. */
const DEFAULT_TIMEOUT_MS = 1000;
/** A flash save blocks the device's main loop for 100-200 ms; be generous. */
const SAVE_SETTLE_TIMEOUT_MS = 3000;
/**
 * `HIDDevice.open()` can simply never settle if the handle is stale -- which is the
 * normal outcome of racing a replug. Without a timeout the UI sits on "Opening the
 * device…" forever with no error and no way out.
 */
const OPEN_TIMEOUT_MS = 5000;

interface Pending {
  readonly what: string;
  resolve(frame: Frame): void;
  reject(error: Error): void;
  timer: ReturnType<typeof setTimeout>;
}

export type SaveResultListener = (result: SaveResult) => void;
export type DisconnectListener = () => void;

/** Reject if `promise` has not settled within `timeoutMs`. */
async function withTimeout<T>(promise: Promise<T>, timeoutMs: number, what: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timer = setTimeout(
          () => reject(new Error(`${what} timed out after ${timeoutMs} ms`)),
          timeoutMs,
        );
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export class Transport {
  private readonly device: HIDDevice;
  private pending: Pending | null = null;
  /** Serialises requests so at most one is in flight. */
  private queue: Promise<unknown> = Promise.resolve();

  private readonly saveListeners = new Set<SaveResultListener>();
  private readonly disconnectListeners = new Set<DisconnectListener>();

  private constructor(device: HIDDevice) {
    this.device = device;
    device.addEventListener('inputreport', this.onInputReport);
  }

  /* ------------------------------------------------------------- selection -- */

  /**
   * `requestDevice({filters})` — shows the chooser. Returns undefined if the user
   * dismissed it or the device is not present.
   */
  static async request(): Promise<Transport | undefined> {
    const devices = await navigator.hid.requestDevice({ filters: [DEVICE_FILTER] });
    const config = devices.find(isConfigInterface);
    return config ? new Transport(config) : undefined;
  }

  /**
   * Wrap a specific HIDDevice, if it is the vendor interface.
   *
   * This is what the `connect` event should use. The event carries the REAL new
   * handle; scanning `getDevices()` instead can pick up the STALE one from before the
   * replug, and opening a stale handle is a hang.
   */
  static fromDevice(device: HIDDevice): Transport | undefined {
    return isConfigInterface(device) ? new Transport(device) : undefined;
  }

  /**
   * `getDevices()` — every device this origin has already been granted, WITHOUT
   * filtering. Includes the mouse interface, which is why the collection check is
   * mandatory here rather than optional.
   */
  static async findAlreadyGranted(): Promise<Transport | undefined> {
    const devices = await navigator.hid.getDevices();
    const config = devices.find(isConfigInterface);
    return config ? new Transport(config) : undefined;
  }

  static async listGranted(): Promise<HIDDevice[]> {
    return navigator.hid.getDevices();
  }

  /* ---------------------------------------------------------------- lifecycle -- */

  get raw(): HIDDevice {
    return this.device;
  }

  get isOpen(): boolean {
    return this.device.opened;
  }

  /** Label for logs and the UI. */
  get label(): string {
    return this.device.productName || `ViMouseJiggler ${this.device.productId.toString(16)}`;
  }

  async open(timeoutMs = OPEN_TIMEOUT_MS): Promise<void> {
    if (this.device.opened) return;

    try {
      await withTimeout(this.device.open(), timeoutMs, `opening ${this.label}`);
    } catch (error) {
      // "Already open" means someone else won the race, which is a success for us.
      if (error instanceof DOMException && error.name === 'InvalidStateError') return;
      if (this.device.opened) return;
      throw error;
    }
  }

  async close(): Promise<void> {
    this.disposed = true;
    if (this.device.opened) {
      // Closing a device that has already gone away throws; there is nothing useful
      // to do about it and the caller is usually tearing down anyway.
      await this.device.close().catch(() => undefined);
    }
  }

  dispose(): void {
    this.disposed = true;
    this.device.removeEventListener('inputreport', this.onInputReport);
    this.saveListeners.clear();
    this.disconnectListeners.clear();
    if (this.pending) {
      clearTimeout(this.pending.timer);
      this.pending.reject(new Error('transport disposed'));
      this.pending = null;
    }
  }

  /** True once close()/dispose() has run: late responses must be ignored. */
  private disposed = false;

  onSaveResult(listener: SaveResultListener): () => void {
    this.saveListeners.add(listener);
    return () => this.saveListeners.delete(listener);
  }

  onDisconnect(listener: DisconnectListener): () => void {
    this.disconnectListeners.add(listener);
    return () => this.disconnectListeners.delete(listener);
  }

  /* ---------------------------------------------------------------- receive -- */

  private readonly onInputReport = (event: HIDInputReportEvent): void => {
    if (this.disposed) return;

    // Report ID 2 is the device -> host response channel. Anything else on this
    // interface is not something we asked for.
    if (event.reportId !== MSG_REPORT_ID_IN) return;

    let frame: Frame;
    try {
      frame = decodeFrame(event.data);
    } catch {
      return; // malformed; dropping is the only safe option
    }

    // SAVE_RESULT is genuinely asynchronous: it arrives after the ACK for the
    // SAVE_TO_FLASH that triggered it, so it must not consume the pending slot.
    if (frame.msgId === RespId.SAVE_RESULT) {
      const result = decodeSaveResult(frame.data);
      for (const listener of this.saveListeners) listener(result);
      return;
    }

    const pending = this.pending;
    if (!pending) {
      // Unsolicited response. Harmless, but worth surfacing during bring-up.
      return;
    }

    this.pending = null;
    clearTimeout(pending.timer);

    if (frame.msgId === RespId.NACK) {
      const reason = frame.data[1] ?? 0;
      const reasonName = NACK_REASON_NAMES[reason] ?? `0x${reason.toString(16)}`;
      pending.reject(
        new DeviceNackError(this.lastSentMsgId, reason, reasonName, NACK_REASON_HELP[reason]),
      );
      return;
    }

    pending.resolve(frame);
  };

  /* ------------------------------------------------------------------ send -- */

  /** The msgId of the request currently in flight, for NACK reporting. */
  private lastSentMsgId = 0;

  private async sendRaw(frame: Uint8Array, what: string, timeoutMs: number): Promise<Frame> {
    if (this.pending) {
      throw new DeviceBusyError(what);
    }
    if (this.disposed) {
      throw new Error(`${what}: the transport was closed`);
    }
    if (!this.device.opened) {
      throw new Error(`${what}: the device is not open`);
    }

    return new Promise<Frame>((resolve, reject) => {
      this.pending = {
        what,
        resolve,
        reject,
        timer: setTimeout(() => {
          this.pending = null;
          reject(new DeviceTimeoutError(what, timeoutMs));
        }, timeoutMs),
      };

      this.device.sendReport(MSG_REPORT_ID_OUT, frame as unknown as BufferSource).catch((err: unknown) => {
        const p = this.pending;
        if (p) {
          clearTimeout(p.timer);
          this.pending = null;
        }
        reject(err instanceof Error ? err : new Error(String(err)));
      });
    });
  }

  /** Serialise everything — see rule 2 at the top of this file. */
  private enqueue<T>(fn: () => Promise<T>): Promise<T> {
    const run = this.queue.then(fn, fn);
    // Swallow rejections on the chain itself so one failure cannot poison the
    // queue for every later request.
    this.queue = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }

  /**
   * Send one request and await its response.
   *
   * `attempts` exists for the FIRST exchange after a connect: on a fresh plug the
   * device may still be finishing enumeration when we open it, and a single 1 s
   * window is easy to lose. Retries only apply before anything has been confirmed.
   */
  private async request(
    msgId: number,
    payload: Uint8Array = new Uint8Array(0),
    timeoutMs = DEFAULT_TIMEOUT_MS,
    attempts = 1,
  ): Promise<Frame> {
    const what = `msgId 0x${msgId.toString(16).padStart(2, '0')}`;

    let lastError: unknown;
    for (let attempt = 1; attempt <= attempts; attempt++) {
      try {
        return await this.enqueue(() => {
          this.lastSentMsgId = msgId;
          return this.sendRaw(encodeFrame(msgId, payload), what, timeoutMs);
        });
      } catch (error) {
        lastError = error;
        // A NACK is a real answer; retrying it would be pointless and confusing.
        if (error instanceof DeviceNackError) throw error;
        if (attempt < attempts) {
          await sleep(120);
        }
      }
    }

    throw lastError instanceof Error ? lastError : new Error(String(lastError));
  }

  /** The initial handshake, with retries. Used once per connection. */
  async hello(): Promise<DeviceInfo> {
    const frame = await this.request(MsgId.GET_INFO, new Uint8Array(0), DEFAULT_TIMEOUT_MS, 3);
    if (frame.msgId !== RespId.INFO) throw new Error('GET_INFO did not return INFO');
    return decodeInfo(frame.data);
  }

  /** Expect an ACK; throw DeviceNackError with the reason on NACK. */
  private async expectAck(msgId: number, payload?: Uint8Array, timeoutMs?: number): Promise<void> {
    const frame = await this.request(msgId, payload, timeoutMs);
    if (frame.msgId !== RespId.ACK) {
      throw new Error(`expected ACK, got response 0x${frame.msgId.toString(16)}`);
    }
  }

  /* --------------------------------------------------------------- commands -- */

  async ping(): Promise<void> {
    await this.expectAck(MsgId.PING);
  }

  async getInfo(): Promise<DeviceInfo> {
    const frame = await this.request(MsgId.GET_INFO);
    if (frame.msgId !== RespId.INFO) throw new Error('GET_INFO did not return INFO');
    return decodeInfo(frame.data);
  }

  async getConfig(): Promise<DeviceConfig> {
    const frame = await this.request(MsgId.GET_CONFIG);
    if (frame.msgId !== RespId.CONFIG) throw new Error('GET_CONFIG did not return CONFIG');
    return decodeConfig(frame.data);
  }

  async setConfig(cfg: DeviceConfig): Promise<void> {
    await this.expectAck(MsgId.SET_CONFIG, encodeConfig(cfg));
  }

  async getStatus(): Promise<DeviceStatus> {
    const frame = await this.request(MsgId.GET_STATUS);
    if (frame.msgId !== RespId.STATUS) throw new Error('GET_STATUS did not return STATUS');
    return decodeStatus(frame.data);
  }

  async start(): Promise<void> {
    await this.expectAck(MsgId.START);
  }

  async stop(): Promise<void> {
    await this.expectAck(MsgId.STOP);
  }

  async factoryReset(): Promise<void> {
    await this.expectAck(MsgId.FACTORY_RESET);
  }

  async loadFromFlash(): Promise<void> {
    await this.expectAck(MsgId.LOAD_FROM_FLASH);
  }

  /**
   * Persist to flash. Resolves on the ACK (tens of ms), NOT on the write
   * completing — the device pushes SAVE_RESULT later. Subscribe with
   * onSaveResult() to learn the outcome.
   */
  async save(): Promise<void> {
    await this.expectAck(MsgId.SAVE_TO_FLASH, undefined, SAVE_SETTLE_TIMEOUT_MS);
  }

  /**
   * Save and wait for the asynchronous SAVE_RESULT, so callers get a settled
   * answer without polling.
   */
  async saveAndWait(): Promise<SaveResult> {
    const settled = new Promise<SaveResult>((resolve, reject) => {
      const timer = setTimeout(() => {
        off();
        reject(new DeviceTimeoutError('SAVE_RESULT', SAVE_SETTLE_TIMEOUT_MS));
      }, SAVE_SETTLE_TIMEOUT_MS);

      const off = this.onSaveResult((result) => {
        clearTimeout(timer);
        off();
        resolve(result);
      });
    });

    await this.save();
    return settled;
  }

  /* --------------------------------------------------------------- patterns -- */

  async beginPattern(kind: number, stepCount: number): Promise<void> {
    if (stepCount > 0xffff) throw new RangeError('stepCount exceeds 65535');
    await this.expectAck(MsgId.PATTERN_BEGIN, new Uint8Array([
      kind,
      stepCount & 0xff,
      (stepCount >> 8) & 0xff,
    ]));
  }

  async sendPatternChunk(index: number, steps: readonly JigStep[]): Promise<void> {
    if (steps.length > MSG_STEPS_PER_FRAME) {
      throw new RangeError(`chunk of ${steps.length} steps exceeds the ${MSG_STEPS_PER_FRAME}-step frame limit`);
    }
    await this.expectAck(MsgId.PATTERN_DATA, encodeSteps(index, steps));
  }

  async endPattern(): Promise<void> {
    await this.expectAck(MsgId.PATTERN_END);
  }

  /**
   * Upload a complete macro. The device stages the steps and only commits them on
   * PATTERN_END, so a failure part-way leaves the previous pattern intact — which
   * matters because the device may be running it.
   */
  async uploadMacro(steps: readonly JigStep[]): Promise<void> {
    await this.beginPattern(0 /* PATTERN_MACRO */, steps.length);
    for (let offset = 0; offset < steps.length; offset += MSG_STEPS_PER_FRAME) {
      await this.sendPatternChunk(offset, steps.slice(offset, offset + MSG_STEPS_PER_FRAME));
    }
    await this.endPattern();
  }

  /** Read the whole live macro, paging 9 steps at a time. */
  async readMacro(): Promise<JigStep[]> {
    const out: JigStep[] = [];
    for (;;) {
      // GET_PATTERN is stateless: each request must carry the next step offset.
      const offset = out.length;
      const frame = await this.request(MsgId.GET_PATTERN, new Uint8Array([
        offset & 0xff,
        (offset >> 8) & 0xff,
        MSG_STEPS_PER_FRAME,
      ]));
      if (frame.msgId !== RespId.PATTERN_CHUNK) {
        throw new Error('GET_PATTERN did not return PATTERN_CHUNK');
      }
      // A zero-count chunk is the documented end-of-data signal, NOT an error.
      const { index, steps } = decodeSteps(frame.data);
      if (steps.length === 0) break;
      if (index !== out.length) {
        throw new Error(`GET_PATTERN returned index ${index} where ${out.length} was expected`);
      }
      out.push(...steps);
      if (steps.length < MSG_STEPS_PER_FRAME) break;
    }
    return out;
  }
}
